/**
 * Chain + contract configuration.
 *
 * Addresses marked VERIFY were taken from public docs/explorers during
 * development research and MUST be re-verified against authoritative sources
 * before real funds are used (BRIEF §35, §39). Everything routes through this
 * file so a wrong assumption is a one-line fix.
 */
import type { Hex } from "./types";

export const GNOSIS_CHAIN = {
  id: 100,
  name: "Gnosis Chain",
  // Public endpoints; the scanner rotates on failure.
  rpcUrls: ["https://rpc.gnosischain.com", "https://gnosis-rpc.publicnode.com", "https://1rpc.io/gnosis"],
  blockTimeSeconds: 5,
  /**
   * getLogs chunk size. Public endpoints differ (Ankr caps ~3k blocks,
   * Chainstack recommends ≤10k); 2 000 ≈ 2.8h of chain and is safe everywhere.
   */
  maxLogRangeBlocks: 2_000,
} as const;

/**
 * Tokens a Gnosis Pay Safe can hold/spend, on Gnosis Chain.
 * Decimals: Monerium e-money tokens are 18dp, USDC.e is 6dp.
 */
export interface SpendToken {
  symbol: string;
  address: Hex;
  decimals: number;
  currency: "GBP" | "EUR" | "USD";
}

/**
 * All three VERIFIED onchain 2026-08-10 via symbol()/name() eth_calls,
 * gnosispay/ui constants and live spend traffic (docs/RESEARCH.md).
 * Currency assignment at Safe deploy: GB → GBPe, BR → USDC.e, rest → EURe.
 */
export const GNOSIS_SPEND_TOKENS: SpendToken[] = [
  { symbol: "GBPe", address: "0x5Cb9073902F2035222B9749F8fB0c9BFe5527108", decimals: 18, currency: "GBP" },
  { symbol: "EURe", address: "0xcB444e90D8198415266c6a2724b7900fb12FC56E", decimals: 18, currency: "EUR" },
  { symbol: "USDC.e", address: "0x2a22f9c3b484c3629090FeED35F17Ff8F88f76F0", decimals: 6, currency: "USD" },
];

/**
 * Gnosis Pay infrastructure addresses on Gnosis Chain.
 * VERIFIED 2026-08-10 against live settlement transactions and the
 * gnosispay/ui + gnosispay/account-kit repos — see docs/RESEARCH.md.
 */
export const GNOSIS_PAY = {
  /**
   * The Gnosis Pay settlement Safe: sole destination of every card-spend
   * Transfer (2,664/2,664 in a 7h live sample) and the source of refunds and
   * reversals. Survived the June 2026 incident migration unchanged. This is
   * the same constant Gnosis Pay's own UI uses to classify card activity.
   */
  settlementAddresses: ["0x4822521E6135CD2599199c83Ea35179229A172EE"] as Hex[],
  /**
   * Active relayer EOA submitting spend txs. ROTATABLE — corroboration
   * signal only, never a hard requirement.
   */
  relayerAddresses: ["0x90830ED558F12D826370dC52E9D87947A7F18De9"] as Hex[],
  /**
   * Spender Modifier (tx.to of every card spend, post-June-2026 deployment).
   * Emits Spend(asset, account, receiver, amount) — useful global watch
   * point, but it has already changed once, so don't depend on it.
   */
  spenderModule: "0x5f07734e2b9c4de6f9c32253d485741800da3f8a" as Hex,
  /** Cashback (GNO) sender — inbound transfers from here are rewards. */
  rewardAddress: "0xCdF50be9061086e2eCfE6e4a1BF9164d43568EEC" as Hex,
} as const;

/**
 * Robinhood Chain mainnet (live since 2026-07-01). Arbitrum Orbit L2, ETH gas.
 * Verified 2026-08-10 against docs.robinhood.com/chain, Paxos USDG docs and
 * robinhoodchain.blockscout.com — see docs/RESEARCH.md for sources.
 */
export const ROBINHOOD_CHAIN = {
  id: 4663,
  name: "Robinhood Chain",
  rpcUrls: ["https://rpc.mainnet.chain.robinhood.com"],
  explorer: "https://robinhoodchain.blockscout.com",
  // Paxos Global Dollar — the chain's canonical stablecoin, VERIFIED (Paxos docs + explorer).
  usdg: { address: "0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168" as Hex, decimals: 6 },
  /**
   * Stock tokens: ERC-20, 18dp, blocklist-gated (not allowlist) — any
   * non-blocked address can hold/transfer. ERC-8056 "Scaled UI Amount":
   * displayed SHARES = raw balance × uiMultiplier() / 1e18 (selector
   * 0xa60bf13d, live-verified 2026-08-11; 1.0 for NVDA today).
   * NVDA VERIFIED on explorer; others from QuickNode's official guide.
   *
   * chainlinkFeed: official per-stock Chainlink price feeds ON Robinhood
   * Chain (AggregatorV3Interface, 8dp, 24/5). The answer is the TOTAL RETURN
   * value per RAW token (multiplier already applied), so raw units × answer
   * values a holding directly. Addresses from Chainlink's reference-data
   * registry, live-verified via description()+latestRoundData() 2026-08-11.
   */
  stockTokens: {
    NVDA: { address: "0xd0601CE157Db5bdC3162BbaC2a2C8aF5320D9EEC" as Hex, decimals: 18, chainlinkFeed: "0x379EC4f7C378F34a1B47E4F3cbeBCbAC3E8E9F15" as Hex },
    AAPL: { address: "0xaF3D76f1834A1d425780943C99Ea8A608f8a93f9" as Hex, decimals: 18, chainlinkFeed: "0x6B22A786bAa607d76728168703a39Ea9C99f2cD0" as Hex },
    TSLA: { address: "0x322F0929c4625eD5bAd873c95208D54E1c003b2d" as Hex, decimals: 18, chainlinkFeed: "0x4A1166a659A55625345e9515b32adECea5547C38" as Hex },
    SPY: { address: "0x117cc2133c37B721F49dE2A7a74833232B3B4C0C" as Hex, decimals: 18, chainlinkFeed: "0x319724394D3A0e3669269846abE664Cd621f9f6A" as Hex },
  } as Record<string, { address: Hex; decimals: number; chainlinkFeed?: Hex }>,
} as const;

/**
 * Below this round-up value we queue rather than bridge (BRIEF §18).
 *
 * Bridge economics verified 2026-08-10: Across can't source from Gnosis;
 * Gnosis→Robinhood goes via LiFi/Relay, where fixed fees make sub-£1
 * transfers cost 3–6% but a ~£5 batch costs ~1.5%. So we accumulate queued
 * round-ups to £5 before executing. (Across minDeposit is ~$0.50 if a
 * Base/Arbitrum two-hop route is used instead.)
 */
export const MIN_VIABLE_BRIDGE_PENCE = 500;

/** Poll interval while the PWA is open. */
export const SCAN_INTERVAL_MS = 5_000;

/**
 * Gnosis Pay partner integration (docs.gnosispay.com/integration-model).
 * Endpoints verified against the live OpenAPI spec (api.gnosispay.com
 * /api-docs/spec.json, fetched 2026-08-11). partnerId is OPTIONAL on signup —
 * the flow works without it; set it once the partner registration
 * (partners.gnosispay.com, self-service) is done so signups attribute to us.
 * Production domains must be whitelisted there for CORS; localhost is
 * auto-allowed.
 */
export const GNOSIS_PAY_API = {
  /**
   * Direct API first (needs our domain in the partner CORS whitelist).
   * In production the client automatically falls back to the same-origin
   * Pages Function proxy (functions/gp/[[path]].js) when a fetch dies at the
   * network layer (CORS not yet propagated). Localhost is always
   * CORS-allowed by Gnosis Pay, so dev never needs the fallback.
   */
  base: "https://api.gnosispay.com",
  proxyBase: "/gp",
  /** Florin's partner registration (partners.gnosispay.com, 2026-08-11). */
  partnerId: "cmsnv3tpl000kxx2kx3lspovk",
  /**
   * PSE App ID (issued in the dashboard's PSE Integration setup). Public
   * identifier; the matching mTLS private key + signed cert live OUTSIDE the
   * repo in ~/florin-secrets/ and are used server-side only (PSE card
   * display — future work).
   */
  appId: "gp_538fc90eef1392871908fbe903adcaa5",
} as const;

/**
 * LiFi routing (live path, verified with live quotes 2026-08-10):
 *   leg 1  USDC.e (Gnosis)  → USDG (4663)   tool: relaydepository, ~3s
 *   leg 2  USDG → stock token, same-chain on 4663, ~80s
 *   gas    USDC.e (Gnosis)  → ETH (4663)    only when the wallet runs dry
 * Direct Gnosis→stock and GBPe routes were checked and are NOT supported,
 * hence the two-leg design and the USDC.e reserve currency.
 */
export const LIFI = {
  apiBase: "https://li.quest/v1",
  /** Optional integrator id for support/attribution; fine to leave blank. */
  integrator: "",
  /** Slippage per leg. Small notionals on thin pairs need headroom. */
  bridgeSlippage: 0.01,
  swapSlippage: 0.03,
  /** How long we poll a leg before declaring it stuck (recovery resumes it). */
  statusTimeoutMs: 10 * 60_000,
  statusPollMs: 5_000,
} as const;

/** The reserve token in live mode: USDC.e on Gnosis (the only verified route). */
export const RESERVE_TOKEN = GNOSIS_SPEND_TOKENS.find((t) => t.symbol === "USDC.e")!;

/**
 * Destination-gas policy (owner decision 2026-08-10): gas on Robinhood Chain
 * is paid FROM the round-up batch — it reduces the invested amount and is
 * never charged on top. When the wallet's 4663 ETH can't cover a swap, a
 * slice of the batch is routed USDC.e→ETH first.
 */
/**
 * Gnosis-side gas (xDAI) policy: self-sustaining after first activation.
 * Every reserve top-up replenishes xDAI FROM the deposit when low, so the
 * user never buys "gas". A tightly authenticated Railway lender supplies the
 * first droplet and the first deposit repays it automatically.
 */
export const GNOSIS_GAS = {
  /** Refill when the wallet's xDAI drops below this (≈40 transactions left). */
  minWei: 20_000_000_000_000_000n, // 0.02 xDAI
  /** USDC.e units converted per refill (6dp) ≈ $1 — lasts months. */
  topUpUnits: 1_000_000n,
  /** Below this the wallet can't even submit a swap — first-gas needed. */
  bootstrapMinWei: 500_000_000_000_000n, // 0.0005 xDAI
} as const;

/**
 * Railway gas lender. The URL is public; only its private signing key is a
 * secret. A build-time override makes preview/staging services possible.
 */
export const GAS_SPONSOR = {
  apiBase:
    (import.meta.env.VITE_GAS_SPONSOR_URL as string | undefined)?.replace(/\/$/, "") ||
    "https://florin-gas-sponsor-production.up.railway.app",
} as const;

export const DEST_GAS = {
  /** Refill when the 4663 ETH balance drops below this (~2 swaps of margin). */
  minWei: 100_000_000_000_000n, // 0.0001 ETH ≈ $0.45
  /** Target slice to convert per refill, USDC.e units (6dp). ≈ £1.60. */
  topUpUnits: 2_000_000n, // $2.00
  /** Never spend more than this share of a batch on gas. */
  maxBatchShare: 0.4,
} as const;
